import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Crown, Sparkles, Star, Check, Loader2, Zap, Shield, Palette, Ban, Clock, ArrowRight, ChevronRight, ImageIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { API_URL } from '@/lib/api';

const TIER_CONFIG = {
  'Quiet Mode': {
    icon: Shield,
    color: 'from-blue-500 to-cyan-400',
    badgeClass: 'bg-blue-500 text-white',
    tagLine: 'Browse in peace. No ads. No noise.',
    pitch: 'Tired of ads? Want a sleek blue ring around your profile picture? Quiet Mode lets you enjoy Secrets Club without the clutter.',
    perks: [
      'No advertisements',
      'Custom profile banner image',
      'Blue ring badge on your profile picture',
      '"Quiet Mode" tag on your profile',
      'Exclusive emoji reactions',
    ],
    price: 1.99,
  },
  'After Hours': {
    icon: Sparkles,
    color: 'from-violet-600 to-indigo-900',
    badgeClass: 'bg-gradient-to-r from-violet-600 to-indigo-900 text-white animate-pulse',
    tagLine: 'Unlock the dark side. Exclusive content awaits.',
    pitch: 'Some secrets are too raw for the main feed. After Hours gives you access to the unfiltered, exclusive content — plus custom themes to make the site your own.',
    perks: [
      'Everything in Quiet Mode',
      'Access to exclusive "After Hours" posts',
      'Custom site themes',
      'Animated space badge',
      '"After Hours" tag on your profile',
      'Even more emoji reactions',
    ],
    price: 9.99,
  },
  'Needlessly Rich': {
    icon: Crown,
    color: 'from-rose-500 to-amber-400',
    badgeClass: 'bg-gradient-to-r from-rose-500 to-amber-400 text-white',
    tagLine: "You don't need this. But you WANT it.",
    pitch: "Let's be honest. This tier exists purely so you can flex on everyone else. Does it do anything extra? No. Will people know you dropped $200/month on an anonymous confession site? Absolutely.",
    perks: [
      'Everything in After Hours',
      'Sparkling ruby badge',
      '"Needlessly Rich" tag on your profile',
      'Top billing on the Contributors page',
      'The ultimate flex. That\'s it. That\'s the perk.',
      'This tier automatically gets better as new tiers roll out below it!',
      'Secret benefits not mentioned here? ... Maybe.',
    ],
    price: 199.99,
  },
};

/* ─── Single Tier Landing Page ─── */
const TierLanding = ({ tierName, onSubscribe, subscribing }) => {
  const config = TIER_CONFIG[tierName];
  if (!config) return null;
  const Icon = config.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-lg mx-auto"
    >
      <Card className="overflow-hidden" data-testid={`tier-landing-${tierName.toLowerCase().replace(/\s+/g, '-')}`}>
        <div className={`h-2 bg-gradient-to-r ${config.color}`} />
        <CardContent className="pt-8 pb-6 space-y-6 text-center">
          <div className={`w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br ${config.color} flex items-center justify-center`}>
            <Icon className="w-8 h-8 text-white" />
          </div>
          <div>
            <h2 className="font-serif text-3xl font-bold mb-2">{tierName}</h2>
            <p className="text-muted-foreground">{config.pitch}</p>
          </div>
          <div className="flex items-baseline justify-center gap-1">
            <span className="text-5xl font-bold">${config.price}</span>
            <span className="text-muted-foreground text-lg">/month</span>
          </div>
          <ul className="space-y-3 text-left max-w-xs mx-auto">
            {config.perks.map((perk, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>{perk}</span>
              </li>
            ))}
          </ul>
          <Button
            className={`w-full max-w-xs bg-gradient-to-r ${config.color} text-white hover:opacity-90 text-base py-5`}
            onClick={() => onSubscribe(tierName)}
            disabled={subscribing}
            data-testid={`subscribe-btn-${tierName.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {subscribing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Zap className="w-4 h-4 mr-2" />
            )}
            {tierName === 'Needlessly Rich' ? 'Become Needlessly Rich' : `Get ${tierName}`}
          </Button>
        </CardContent>
      </Card>
    </motion.div>
  );
};

/* ─── Admin/Premium Perks Management ─── */
const PerksManagement = ({ user, membership, colorTheme, setColorTheme, availableThemes, onCancel, cancelling, onSubscribe, subscribing }) => {
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const hasThemes = user?.premium_permissions?.includes('custom_themes') || isAdmin;
  const hasBanner = user?.premium_permissions?.includes('custom_banner') || isAdmin;
  const [uploading, setUploading] = useState(false);

  const handleThemeChange = async (themeId) => {
    setColorTheme(themeId);
    try {
      await axios.put(`${API_URL}/api/users/me`, { color_theme: themeId });
    } catch (err) {
      console.error('Failed to save theme preference:', err);
    }
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API_URL}/api/auth/me/banner`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Banner image updated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload banner');
    } finally {
      setUploading(false);
    }
  };

  const currentTier = membership?.premium_tier || 0;
  const allTiers = membership?.available_tiers || [];

  return (
    <div className="space-y-6" data-testid="perks-management">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
        <Palette className="w-10 h-10 mx-auto text-primary mb-3" />
        <h1 className="font-serif text-3xl font-bold mb-1">Premium Features</h1>
        <p className="text-muted-foreground">Manage your premium perks and customization</p>
      </motion.div>

      {/* Current tier info */}
      {membership?.premium_role_name && (
        <Card data-testid="current-membership-card">
          <CardContent className="py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {(() => {
                const config = TIER_CONFIG[membership.premium_role_name] || {};
                const Icon = config.icon || Star;
                return (
                  <>
                    <div className={`p-2 rounded-xl bg-gradient-to-br ${config.color || 'from-gray-400 to-gray-600'}`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{membership.premium_role_name}</h3>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        <span>Member for {membership.tenure_days || 0} day{membership.tenure_days !== 1 ? 's' : ''}</span>
                      </div>
                      {membership.premium_cancelled && membership.premium_expires_at && (
                        <div className="flex items-center gap-1 text-xs text-amber-500 mt-1" data-testid="cancellation-notice">
                          <Clock className="w-3 h-3" />
                          <span>Cancels {new Date(membership.premium_expires_at).toLocaleDateString()} — active until then</span>
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
            {!membership.premium_cancelled && (
              <Button variant="outline" size="sm" className="text-destructive" onClick={onCancel} disabled={cancelling} data-testid="cancel-subscription-btn">
                {cancelling ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Ban className="w-4 h-4 mr-1" />}
                Cancel
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Banner upload */}
      {hasBanner && (
        <Card data-testid="banner-upload-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ImageIcon className="w-5 h-5" /> Custom Profile Banner
            </CardTitle>
          </CardHeader>
          <CardContent>
            {user?.banner_image && (
              <div className="mb-3 rounded-lg overflow-hidden h-24">
                <img src={`${API_URL}${user.banner_image}`} alt="Current banner" className="w-full h-full object-cover" />
              </div>
            )}
            <label
              className="flex items-center justify-center gap-2 border-2 border-dashed rounded-lg p-4 cursor-pointer hover:border-primary/50 transition-colors"
              data-testid="banner-upload-label"
            >
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImageIcon className="w-5 h-5 text-muted-foreground" />}
              <span className="text-sm text-muted-foreground">{uploading ? 'Uploading...' : 'Click to upload a banner image'}</span>
              <input type="file" accept="image/*" className="hidden" onChange={handleBannerUpload} disabled={uploading} />
            </label>
          </CardContent>
        </Card>
      )}

      {/* Theme selector */}
      {hasThemes && (
        <Card data-testid="theme-selector-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Palette className="w-5 h-5" /> Site Theme
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {availableThemes.map((t) => (
                <button
                  key={t.theme_id}
                  onClick={() => handleThemeChange(t.theme_id)}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    colorTheme === t.theme_id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                  }`}
                  data-testid={`theme-option-${t.theme_id}`}
                >
                  <div className="font-medium text-sm">{t.name}</div>
                  <div className="text-xs text-muted-foreground">{t.description}</div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tier switching — show available upgrades/changes */}
      {!isAdmin && allTiers.length > 0 && (
        <Card data-testid="tier-switching-card">
          <CardHeader>
            <CardTitle className="text-lg">Change Tier</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {allTiers.map(tier => {
                const config = TIER_CONFIG[tier.name] || {};
                const Icon = config.icon || Star;
                const isCurrent = tier.name === membership?.premium_role_name;
                const isUpgrade = tier.tier_level > currentTier;
                return (
                  <div key={tier.name} className={`flex items-center justify-between p-3 rounded-lg border ${isCurrent ? 'border-primary bg-primary/5' : 'border-border'}`} data-testid={`switch-tier-${tier.name.toLowerCase().replace(/\s+/g, '-')}`}>
                    <div className="flex items-center gap-3">
                      <div className={`p-1.5 rounded-lg bg-gradient-to-br ${config.color || 'from-gray-400 to-gray-600'}`}>
                        <Icon className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <span className="font-medium">{tier.name}</span>
                        <span className="text-sm text-muted-foreground ml-2">${tier.monthly_price}/mo</span>
                      </div>
                    </div>
                    {isCurrent ? (
                      <Badge variant="outline" className="text-xs">Current</Badge>
                    ) : isUpgrade ? (
                      <Button size="sm" className={`bg-gradient-to-r ${config.color || 'from-gray-500 to-gray-700'} text-white hover:opacity-90`} onClick={() => onSubscribe(tier.name)} disabled={subscribing} data-testid={`upgrade-to-${tier.name.toLowerCase().replace(/\s+/g, '-')}`}>
                        {subscribing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Zap className="w-3 h-3 mr-1" />}
                        Upgrade
                      </Button>
                    ) : (
                      <span className="text-xs text-muted-foreground">Included</span>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment history */}
      {membership?.recent_transactions?.length > 0 && (
        <Card data-testid="transaction-history">
          <CardHeader><CardTitle className="text-lg">Payment History</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {membership.recent_transactions.map((txn, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <div>
                    <span className="font-medium">{txn.tier_name}</span>
                    <span className="text-sm text-muted-foreground ml-2">{new Date(txn.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">${txn.amount}</span>
                    <Badge variant={txn.payment_status === 'paid' ? 'default' : 'secondary'} className="text-xs">{txn.payment_status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

/* ─── Main Membership Page ─── */
export default function MembershipPage() {
  const { tier: tierParam } = useParams();
  const { user, checkAuth } = useAuth();
  const { colorTheme, setColorTheme, availableThemes } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [pollingStatus, setPollingStatus] = useState(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const hasPremium = (membership?.premium_tier || 0) > 0;

  const fetchMembership = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/membership`);
      setMembership(res.data);
    } catch (err) {
      console.error('Failed to load membership:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMembership(); }, [fetchMembership]);

  // Poll for checkout status
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) return;

    let attempts = 0;
    setPollingStatus('checking');

    const poll = async () => {
      if (attempts >= 10) {
        setPollingStatus('timeout');
        toast.error('Payment verification timed out. Please check back shortly.');
        return;
      }
      attempts++;
      try {
        const res = await axios.get(`${API_URL}/api/stripe/checkout-status/${sessionId}`);
        if (res.data.payment_status === 'paid') {
          setPollingStatus('success');
          toast.success(`Welcome to ${res.data.tier_name}!`);
          setSearchParams({});
          await fetchMembership();
          if (checkAuth) checkAuth();
          return;
        } else if (res.data.status === 'expired') {
          setPollingStatus('expired');
          toast.error('Session expired.');
          setSearchParams({});
          return;
        }
        setTimeout(poll, 2000);
      } catch (err) {
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [searchParams, setSearchParams, fetchMembership, checkAuth]);

  const handleSubscribe = async (tierName) => {
    setSubscribing(true);
    try {
      const res = await axios.post(`${API_URL}/api/stripe/create-checkout`, {
        tier_name: tierName,
        origin_url: window.location.origin,
      });
      if (res.data.url) {
        window.location.href = res.data.url;
        return;
      }
      if (res.data.updated) {
        toast.success(res.data.message || `Updated to ${res.data.tier_name}`);
        await fetchMembership();
        if (checkAuth) checkAuth();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setSubscribing(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel your subscription? Your premium benefits will remain active until the end of your current billing period.')) return;
    setCancelling(true);
    try {
      await axios.post(`${API_URL}/api/membership/cancel`);
      toast.success('Subscription cancelled');
      await fetchMembership();
      if (checkAuth) checkAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;
  }

  // Determine which tier page to show
  const tierPageMap = {
    'quiet-mode': 'Quiet Mode',
    'after-hours': 'After Hours',
    'needlessly-rich': 'Needlessly Rich',
  };
  const requestedTier = tierParam ? tierPageMap[tierParam] : null;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-8" data-testid="membership-page">
      {/* Payment polling */}
      {pollingStatus === 'checking' && (
        <Card className="border-blue-400/50 bg-blue-50/50 dark:bg-blue-950/20">
          <CardContent className="py-4 flex items-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
            <span className="font-medium">Verifying your payment...</span>
          </CardContent>
        </Card>
      )}

      {/* Case 1: Specific tier landing page requested */}
      {requestedTier && !isAdmin && !hasPremium ? (
        <TierLanding tierName={requestedTier} onSubscribe={handleSubscribe} subscribing={subscribing} />
      ) : requestedTier && (isAdmin || hasPremium) ? (
        <PerksManagement user={user} membership={membership} colorTheme={colorTheme} setColorTheme={setColorTheme} availableThemes={availableThemes} onCancel={handleCancel} cancelling={cancelling} onSubscribe={handleSubscribe} subscribing={subscribing} />
      ) : isAdmin || hasPremium ? (
        <PerksManagement user={user} membership={membership} colorTheme={colorTheme} setColorTheme={setColorTheme} availableThemes={availableThemes} onCancel={handleCancel} cancelling={cancelling} onSubscribe={handleSubscribe} subscribing={subscribing} />
      ) : (
        // Case 3: Non-member, no specific tier — show Quiet Mode landing (default)
        <>
          <TierLanding tierName="Quiet Mode" onSubscribe={handleSubscribe} subscribing={subscribing} />
          <Card className="border-dashed text-center">
            <CardContent className="py-5 text-sm text-muted-foreground">
              Looking for more?{' '}
              <Link to="/membership/after-hours" className="text-violet-500 hover:underline font-medium" data-testid="browse-after-hours">
                After Hours ($9.99/mo) <ArrowRight className="w-3 h-3 inline" />
              </Link>
              {' · '}
              <Link to="/membership/needlessly-rich" className="text-amber-500 hover:underline font-medium" data-testid="browse-needlessly-rich">
                Needlessly Rich ($199.99/mo) <ArrowRight className="w-3 h-3 inline" />
              </Link>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
